/**
 * RecordedVideo: player for interview recordings (intro selfie, question videos).
 *
 * The frame is rendered at its final size from the first paint, so nothing
 * jumps when the video arrives. While the source resolves and the first frame
 * buffers, the frame breathes quietly (no spinner, no text); the video then
 * fades in. A stall during playback shows a thin sliding bar on the top edge.
 *
 * Pure UI: source selection lives in hooks/useStreamableVideo.
 */
import { useEffect, useState } from 'react';
import type { Ref, SyntheticEvent } from 'react';
import { Play } from 'lucide-react';
import type { StreamableVideo } from '../../../../hooks/useStreamableVideo';

// Some browsers (iOS Safari) never buffer before the first tap; reveal the
// player anyway so its controls are reachable.
const REVEAL_FALLBACK_MS = 6000;

export interface RecordedVideoProps {
  video: StreamableVideo;
  /** Accessible name while the video is loading. */
  loadingLabel: string;
  /** Shown inside the frame when the video cannot be loaded. */
  unavailableLabel: string;
  /** Sizing of the frame (e.g. "w-full max-w-3xl aspect-video"). */
  className?: string;
  videoRef?: Ref<HTMLVideoElement>;
  onLoadedMetadata?: (event: SyntheticEvent<HTMLVideoElement>) => void;
  onTimeUpdate?: (event: SyntheticEvent<HTMLVideoElement>) => void;
}

export const RecordedVideo = ({
  video,
  loadingLabel,
  unavailableLabel,
  className = 'w-full aspect-video',
  videoRef,
  onLoadedMetadata,
  onTimeUpdate,
}: RecordedVideoProps) => {
  const src = video.status === 'loaded' ? video.src : null;
  // Tied to the source, so a new or refreshed source starts hidden again.
  const [readySrc, setReadySrc] = useState<string | null>(null);
  const [isBuffering, setIsBuffering] = useState(false);
  const isReady = src !== null && readySrc === src;
  const isUnavailable = video.status === 'error';

  useEffect(() => {
    if (!src) return;
    const timer = window.setTimeout(() => setReadySrc(src), REVEAL_FALLBACK_MS);
    return () => window.clearTimeout(timer);
  }, [src]);

  // Hold the reveal while a start-position seek is in flight, so the first
  // visible frame is the one playback starts from.
  const reveal = (event: SyntheticEvent<HTMLVideoElement>) => {
    if (!event.currentTarget.seeking) setReadySrc(src);
  };
  const stopBuffering = () => setIsBuffering(false);

  return (
    <div
      className={`relative overflow-hidden rounded-lg bg-dark-invirtus ${className}`}
      aria-busy={!isReady && !isUnavailable}
      aria-label={!isReady && !isUnavailable ? loadingLabel : undefined}
    >
      {src && (
        <video
          ref={videoRef}
          src={src}
          controls
          playsInline
          preload="metadata"
          className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-300 motion-reduce:transition-none ${
            isReady ? 'opacity-100' : 'opacity-0'
          }`}
          onLoadedMetadata={(event) => {
            onLoadedMetadata?.(event);
            video.onLoadedMetadata(event);
          }}
          // First frame decoded (after any start-position seek), or the
          // browser chose not to buffer yet: show the player.
          onLoadedData={reveal}
          onCanPlay={(event) => {
            reveal(event);
            stopBuffering();
          }}
          onSuspend={reveal}
          onTimeUpdate={onTimeUpdate}
          onWaiting={(event) => {
            if (!event.currentTarget.paused) setIsBuffering(true);
          }}
          onPlaying={stopBuffering}
          onPause={stopBuffering}
          onSeeked={(event) => {
            reveal(event);
            stopBuffering();
          }}
          onEmptied={stopBuffering}
          onError={(event) => {
            stopBuffering();
            video.onError(event);
          }}
        />
      )}

      {!isReady && !isUnavailable && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 flex items-center justify-center bg-dark-invirtus animate-pulse motion-reduce:animate-none"
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-light/10">
            <Play className="h-5 w-5 translate-x-px text-light/40" fill="currentColor" strokeWidth={0} />
          </span>
        </div>
      )}

      {isUnavailable && (
        <div className="absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-light/70 font-body">
          {unavailableLabel}
        </div>
      )}

      {isReady && isBuffering && (
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-0.5 overflow-hidden">
          <div className="h-full w-1/4 bg-purple animate-[progress-indeterminate_1.5s_ease-in-out_infinite] motion-reduce:animate-none" />
        </div>
      )}
    </div>
  );
};
